'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Calendar, RefreshCw, Menu, X, Zap } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export default function DashboardHeader() {
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = [
    { label: t?.nav?.home ?? 'Home', href: '/landing', icon: '🌐', desc: t?.landing?.liveCoverage ?? 'Landing page' },
    { label: t?.nav?.dashboard ?? 'Dashboard', href: '/live-games-dashboard', icon: '📊', desc: t?.dashboard?.title ?? 'Live games dashboard' },
    { label: t?.nav?.liveScores ?? 'Live Scores', href: '/live-scores', icon: '⚡', desc: t?.liveScores?.subtitle ?? 'Real-time match scores' },
    { label: t?.nav?.wcNews ?? 'WC News', href: '/news', icon: '📰', desc: t?.news?.subtitle ?? 'World Cup news & stories' },
    { label: t?.nav?.standings ?? 'Standings', href: '/standings', icon: '🏆', desc: t?.standings?.subtitle ?? 'League tables' },
    { label: t?.nav?.bracket ?? 'Bracket', href: '/bracket', icon: '🔀', desc: t?.bracket?.subtitle ?? 'Tournament bracket' },
    { label: t?.nav?.predictions ?? 'Predictions', href: '/predictions', icon: '🎯', desc: t?.predictions?.subtitle ?? 'Match predictions' },
    { label: t?.nav?.pastGames ?? 'Past Games', href: '/past-games', icon: '🕐', desc: t?.pastGames?.subtitle ?? 'Completed match results' },
    { label: t?.nav?.favorites ?? 'Favorites', href: '/favorites', icon: '⭐', desc: t?.favorites?.subtitle ?? 'Your saved teams' },
    { label: t?.nav?.matchDetail ?? 'Match Detail', href: '/game-detail', icon: '📋', desc: t?.team?.overview ?? 'Detailed match stats' },
    { label: t?.nav?.liveChat ?? 'Live Chat', href: '/live-chat', icon: '💬', desc: t?.chat?.subtitle ?? 'Fan live chat' },
  ];

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">{t?.dashboard?.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Calendar size={13} className="text-slate-500" />
            <span className="text-sm text-slate-400">{t?.dashboard?.matchday} 18 · {t?.dashboard?.groupStage} · April 2, 2026</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Refresh indicator — always visible */}
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
            <RefreshCw size={11} className="text-cyan-500" />
            <span>{t?.dashboard?.updatedJustNow}</span>
          </div>

          {/* Hamburger button — mobile only */}
          <button
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 active:scale-95"
            style={{
              background: mobileMenuOpen ? 'hsl(25 95% 53% / 0.15)' : 'hsl(222 35% 14%)',
              border: `1px solid ${mobileMenuOpen ? 'hsl(25 95% 53% / 0.4)' : 'hsl(222 25% 22%)'}`
            }}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen
              ? <X size={20} className="text-orange-400" />
              : <Menu size={20} className="text-slate-200" />
            }
          </button>
        </div>
      </div>
      {/* ── Mobile Dropdown Menu ── */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop overlay */}
          <div
            className="lg:hidden fixed inset-0 z-40"
            style={{ background: 'hsl(222 47% 4% / 0.6)', backdropFilter: 'blur(4px)', top: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          />

          <div
            className="lg:hidden fixed left-3 right-3 z-50 rounded-2xl overflow-hidden"
            style={{
              top: '80px',
              background: 'hsl(222 40% 8%)',
              border: '1px solid hsl(222 25% 18%)',
              boxShadow: '0 20px 60px hsl(222 47% 3% / 0.8), 0 0 0 1px hsl(222 25% 20% / 0.3)',
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto'
            }}
          >
            {/* Menu header */}
            <div className="px-5 pt-5 pb-3">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'hsl(222 25% 45%)' }}>{t?.dashboard?.navigation ?? 'Navigation'}</p>
            </div>

            {/* Menu items */}
            <div className="px-3 pb-3 flex flex-col gap-1">
              {menuItems?.map((link) => (
                <Link
                  key={link?.href}
                  href={link?.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-4 px-4 py-4 rounded-xl transition-all duration-150 active:scale-[0.98] group"
                  style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
                >
                  <span
                    className="text-xl leading-none w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: 'hsl(222 35% 15%)' }}
                  >
                    {link?.icon}
                  </span>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-white leading-tight">{link?.label}</span>
                    <span className="text-xs mt-0.5" style={{ color: 'hsl(222 25% 50%)' }}>{link?.desc}</span>
                  </div>
                  <svg
                    className="ml-auto flex-shrink-0 opacity-30 group-hover:opacity-60 transition-opacity"
                    width="16" height="16" viewBox="0 0 16 16" fill="none"
                  >
                    <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              ))}
            </div>

            {/* Divider */}
            <div className="mx-5 mb-3" style={{ height: '1px', background: 'hsl(222 25% 16%)' }} />

            {/* Live Scores CTA */}
            <div className="px-3 pb-4">
              <Link
                href="/live-scores"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2.5 px-4 py-4 rounded-xl text-sm font-bold text-white transition-all duration-150 active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, hsl(25 95% 53%), hsl(25 95% 43%))', boxShadow: '0 4px 20px hsl(25 95% 53% / 0.35)' }}
              >
                <Zap size={16} />
                {t?.nav?.liveScores ?? 'Live Scores'}
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  );
}